import React, { useEffect, useState } from "react";
import {
  Button,
  CardActions,
  Card,
  CardHeader,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  useTheme,
  Link,
  IconButton,
  MenuItem,
} from "@mui/material";

import StyledMenu from "./StyledMenu";
import ModalLoadingFull from "../modals/ModalLoadingFull";
import { BCPEllipsisVertical } from "../icons/bcp/BCPIcons";
import moment from "moment";
import { getData } from "../utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function PostCard(props) {
  const {
    title,
    subTitle,
    permalink,
    tags,
    body,
    featuredImageId,
    featuredImage,
    status,
    id,
    sortDate,
  } = props.post;

  const { editMode } = props;

  const theme = useTheme();
  const [photo, setPhoto] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  // Used to create a summary of the first few words of the card
  function trimString(str, characterLimit) {
    if (!str || str === "") {
      return;
    }
    // Regex removes any line that starts with a "#" character (This is done in case the post contains headers)
    str = str.replace(/^#[^\n]*\n/gm, "");

    // Regex removes any image references enclosed in markdown format ![alt text](image URL)
    str = str.replace(/!\[.*?\]\(.*?\)/g, "");

    // Regex removes any link references enclosed in markdown format [link text](link URL)
    str = str.replace(/\[(.*?)\]\(.*?\)/g, "$1");

    // Regex removes text surrounded by two tildes (strikethrough) in markdown format
    str = str.replace(/~~(.*?)~~/g, "");

    // Regex removes asterisks used for emphasis or bold in markdown format
    str = str.replace(/\*+/g, "");

    if (str.length > characterLimit) {
      // Trims the string at the last space before the character limit
      str = str.substring(0, characterLimit).replace(/\s+\S*$/, "");
      // Adds an ellipses at the end of the trimmed string
      str += "...";
    }
    return str;
  }

  // Checks if the post has a featured image, if not then sets the default BCP image for 'photo'
  useEffect(() => {
    const go = async () => {
      setPhoto(null);

      if (featuredImage?.cloudFrontDomain) {
        setPhoto(
          `${featuredImage.cloudFrontDomain}${
            featuredImage.sizes?.small ||
            featuredImage.sizes?.medium ||
            featuredImage.sizes?.large ||
            featuredImage.filename
          }`
        );
      } else if (featuredImageId) {
        try {
          let apiPhoto = await getData(`/files/${featuredImageId}`);
          setPhoto(apiPhoto);
        } catch (err) {
          console.log(err);
        }
      } else {
        setPhoto(
          `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/EventImage${
            theme?.palette?.mode === "dark" ? "Dark" : ""
          }.png`
        );
      }
    };

    go();
    // eslint-disable-next-line
  }, [props]);

  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <CardActionArea
          pb={1}
          href={status === "published" ? `/${permalink}` : `/cms/post/${id}`}
        >
          <CardMedia
            component="img"
            image={photo?.url || photo}
            title={title}
            alt={title}
            sx={{
              backgroundColor: "#222222",
              maxHeight: "calc((100vw - 24px) * 0.5625)",
              "@media (min-width: 600px)": {
                maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)",
              },
              "@media (min-width: 900px)": {
                maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)",
              },
              "@media (min-width: 1218px)": {
                maxHeight: "215px",
              },
            }}
          />

          <CardHeader
            title={
              <>
                <Typography
                  gutterBottom
                  sx={{
                    m: 0,
                    // mt: 1,
                    fontWeight: "700",
                    fontSize: "20px",
                    lineHeight: "1.334",
                  }}
                >
                  {title}
                </Typography>

                {subTitle && (
                  <Typography
                    gutterBottom
                    variant="caption"
                    sx={{
                      fontWeight: "400",
                      fontSize: "16px",
                      lineHeight: "1.334",
                    }}
                  >
                    {subTitle}
                  </Typography>
                )}

                {editMode && (
                  <>
                    <br />
                    <Typography
                      variant="body2"
                      color="white"
                      sx={{
                        borderRadius: "4px",
                        pr: 1,
                        pl: 1,
                        pt: 0.25,
                        pb: 0.25,
                        fontWeight: "400",
                        fontSize: "16px",
                        lineHeight: "1.334",
                        backgroundColor:
                          status === "published"
                            ? theme?.palette?.primary?.main
                            : theme?.palette?.warning.main,
                        display: "inline",
                      }}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Typography>
                  </>
                )}

                {sortDate && (
                  <>
                    <br />
                    <Typography
                      gutterBottom
                      variant="caption"
                      sx={{
                        fontWeight: "400",
                        fontSize: "16px",
                        lineHeight: "1.334",
                        color: "text.disabled",
                      }}
                    >
                      {moment(sortDate).format("LL")}
                    </Typography>
                  </>
                )}
              </>
            }
            action={
              editMode ? (
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    setOptionsAnchorEl(e.currentTarget);
                  }}
                >
                  <BCPEllipsisVertical />
                </IconButton>
              ) : null
            }
          />
        </CardActionArea>

        <CardContent>
          <Typography
            gutterBottom
            variant="body2"
            color="text.secondary"
            sx={{ mt: -2 }}
          >
            {body && body !== "" && trimString(body, 250)}
          </Typography>
        </CardContent>

        {/* Tag section */}
        {tags?.length > 0 && (
          <CardContent
            sx={{
              "& a:not(:first-of-type)": {
                ml: 1,
              },
              p: 2,
              pt: 0,
            }}
          >
            {tags.slice(0, 3).map((tag, index) => {
              return (
                <Link key={index} href={`/news/${tag}`}>
                  {`#${tag}`}
                </Link>
              );
            })}
          </CardContent>
        )}

        <CardActionArea
          href={status === "published" ? `/${permalink}` : `/cms/post/${id}`}
          target="_blank"
        >
          <CardActions>
            <Button
              size="small"
              fullWidth
              color={theme.palette?.mode === "dark" ? "secondary" : "primary"}
            >
              {status === "published" ? "Read More" : "Edit Post"}
            </Button>
          </CardActions>
        </CardActionArea>
      </Card>

      {editMode && (
        <>
          <OptionsMenu
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            post={props.post}
          />
        </>
      )}
    </>
  );
}

function OptionsMenu(props) {
  const { anchorEl, setAnchorEl, post } = props;

  const open = Boolean(anchorEl);
  const [loading] = useState(false);

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button",
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          href={`/cms/post/${post.id}`}
          target="_blank"
          component={"a"}
          disableRipple
        >
          Edit Post
        </MenuItem>
      </StyledMenu>
      {loading && <ModalLoadingFull />}
    </>
  );
}
